import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Input, Select } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import startCase from 'lodash/startCase';

import GetField from '../Components/GetField';
import styles from '../styles';
import { clearActiveJobchartIds, getActiveJobchartIds } from '../Actions';
import constants from '../constants';
import Api from '../Api';
import { errorNotification } from '../Snackbar';

const AntSelect = GetField(Select);
const AntInput = GetField(Input);
const { Option } = Select;

const StyleForm = props => {
  const {
    classes,
    getValues,
    setValue,
    control,
    watch,
    styleIndex,
    styleAppend,
    styleRemove,
  } = props;
  const dispatch = useDispatch();

  const customers = useSelector(state => state.customers);
  const activeJobchartIds = useSelector(state => state.activeJobchartIds);

  const [stylesTable, setStylesTable] = useState({
    quantities: {},
    data: {
      products: [],
      styles: {},
    },
  });

  const GetStyles = useCallback(async id => {
    try {
      const { data } = await Api.get(
        `/jobcharts/stylesfordailycompletion/${id}`
      );

      setStylesTable(data);
    } catch (error) {
      errorNotification(error.message);
    }
  }, []);

  useEffect(() => {
    const customerId = getValues(`styles[${styleIndex}].customerId`);
    if (!customerId) {
      dispatch(clearActiveJobchartIds());
      setValue('jobchartId', null);
    } else {
      dispatch(getActiveJobchartIds({ customerId }));
    }
  }, [watch(`styles[${styleIndex}].customerId`)]);

  useEffect(() => {
    const jobchartId = getValues(`styles[${styleIndex}].jobchartId`);
    if (!jobchartId) {
      setValue('productId', null);
    } else {
      GetStyles(jobchartId);
    }
  }, [watch(`styles[${styleIndex}].jobchartId`)]);

  const stylesArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);

    if (stylesTable.data.styles[productId] !== undefined) {
      return stylesTable.data.styles[productId].styleNames || [];
    }
    return [];
  }, [stylesTable, watch(`styles[${styleIndex}].productId`)]);

  const descriptionsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const style = getValues(`styles[${styleIndex}].style`);

    if (stylesTable.data.styles[productId] !== undefined) {
      return stylesTable.data.styles[productId].descriptions[style] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
  ]);

  const sizesArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const style = getValues(`styles[${styleIndex}].style`);
    const description = getValues(`styles[${styleIndex}].description`);

    if (stylesTable.data.styles[productId]?.sizes[style] !== undefined) {
      return stylesTable.data.styles[productId].sizes[style][description] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
  ]);

  const baseColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);

    if (stylesTable.data.styles[productId] !== undefined) {
      return stylesTable.data.styles[productId].baseColors[styleId] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
  ]);

  const printColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);

    if (
      stylesTable.data.styles[productId]?.printColors[styleId]?.[baseColorId]
    ) {
      return (
        stylesTable.data.styles[productId].printColors[styleId][baseColorId] ||
        []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
  ]);

  const embroideryColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);
    const printColorId = getValues(`styles[${styleIndex}].printColorId`);

    if (
      stylesTable.data.styles[productId]?.embroideryColors[styleId]?.[
        baseColorId
      ]?.[printColorId]
    ) {
      return (
        stylesTable.data.styles[productId].embroideryColors[styleId][
          baseColorId
        ][printColorId] || []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
    watch(`styles[${styleIndex}].printColorId`),
  ]);

  const acidWashColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);
    const printColorId = getValues(`styles[${styleIndex}].printColorId`);
    const embroideryColorId = getValues(
      `styles[${styleIndex}].embroideryColorId`
    );

    if (
      stylesTable.data.styles[productId]?.acidWashColors[styleId]?.[
        baseColorId
      ]?.[printColorId]?.[embroideryColorId]
    ) {
      return (
        stylesTable.data.styles[productId].acidWashColors[styleId][baseColorId][
          printColorId
        ][embroideryColorId] || []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
    watch(`styles[${styleIndex}].printColorId`),
    watch(`styles[${styleIndex}].embroideryColorId`),
  ]);

  useEffect(() => {
    setValue(
      `styles[${styleIndex}].jobchartQuantity`,
      stylesTable.quantities[
        `P${watch(`styles[${styleIndex}].productId`)}S${watch(
          `styles[${styleIndex}].styleId`
        )}B${watch(`styles[${styleIndex}].baseColorId`)}P${
          watch(`styles[${styleIndex}].printColorId`) || ''
        }E${watch(`styles[${styleIndex}].embroideryColorId`) || ''}A${
          watch(`styles[${styleIndex}].acidWashColorId`) || ''
        }`
      ] || 0
    );
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
    watch(`styles[${styleIndex}].printColorId`),
    watch(`styles[${styleIndex}].embroideryColorId`),
    watch(`styles[${styleIndex}].acidWashColorId`),
  ]);

  return (
    <tr>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].customerId`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select customer"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].jobchartId`, null);
                setValue(`styles[${styleIndex}].productId`, null);
                setValue(`styles[${styleIndex}].style`, null);
                setValue(`styles[${styleIndex}].description`, null);
                setValue(`styles[${styleIndex}].styleId`, null);
                setValue(`styles[${styleIndex}].baseColorId`, null);
                setValue(`styles[${styleIndex}].printColorId`, null);
                setValue(`styles[${styleIndex}].embroideryColorId`, null);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.name}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].jobchartId`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select bpo"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].productId`, null);
                setValue(`styles[${styleIndex}].style`, null);
                setValue(`styles[${styleIndex}].description`, null);
                setValue(`styles[${styleIndex}].styleId`, null);
                setValue(`styles[${styleIndex}].baseColorId`, null);
                setValue(`styles[${styleIndex}].printColorId`, null);
                setValue(`styles[${styleIndex}].embroideryColorId`, null);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {activeJobchartIds.map(jobchart => (
                <Option key={jobchart.id} value={jobchart.id}>
                  {jobchart.bpoNo}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].productId`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select product"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].style`, null);
                setValue(`styles[${styleIndex}].description`, null);
                setValue(`styles[${styleIndex}].styleId`, null);
                setValue(`styles[${styleIndex}].baseColorId`, null);
                setValue(`styles[${styleIndex}].printColorId`, null);
                setValue(`styles[${styleIndex}].embroideryColorId`, null);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {stylesTable.data.products.map(({ name, id }) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].style`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select style"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].description`, null);
                setValue(`styles[${styleIndex}].styleId`, null);
                setValue(`styles[${styleIndex}].baseColorId`, null);
                setValue(`styles[${styleIndex}].printColorId`, null);
                setValue(`styles[${styleIndex}].embroideryColorId`, null);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {stylesArray.map(style => (
                <Option key={style} value={style}>
                  {style}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].description`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select description"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].styleId`, null);
                setValue(`styles[${styleIndex}].baseColorId`, null);
                setValue(`styles[${styleIndex}].printColorId`, null);
                setValue(`styles[${styleIndex}].embroideryColorId`, null);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {descriptionsArray.map(description => (
                <Option key={description} value={description}>
                  {description}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].styleId`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select size"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].baseColorId`, null);
                setValue(`styles[${styleIndex}].printColorId`, null);
                setValue(`styles[${styleIndex}].embroideryColorId`, null);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {sizesArray.map(size => (
                <Option key={size.id} value={size.id}>
                  {size.size}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].baseColorId`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select base color"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].printColorId`, null);
                setValue(`styles[${styleIndex}].embroideryColorId`, null);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {baseColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].printColorId`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select print color"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].embroideryColorId`, null);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {printColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].embroideryColorId`}
          control={control}
          render={({ field }) => (
            <AntSelect
              placeholder="Select color"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`styles[${styleIndex}].acidWashColorId`, null);
              }}
            >
              {embroideryColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].acidWashColorId`}
          control={control}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {acidWashColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].jobchartQuantity`}
          control={control}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Jobchart Qty"
              disabled
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].process`}
          control={control}
          render={({ field }) => (
            <AntSelect placeholder="Select process" {...field}>
              {Object.values(constants.workerProcesses).map(process => (
                <Option key={process} value={process}>
                  {startCase(process)}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].quantity`}
          control={control}
          render={({ field }) => (
            <AntInput
              placeholder="Quantity"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`styles[${styleIndex}].remark`}
          control={control}
          render={({ field }) => <AntInput placeholder="Remark" {...field} />}
        />
      </td>
      <td className={classes.inputTd}>
        <DeleteOutlined
          onClick={() => {
            styleRemove(styleIndex);
          }}
        />
        &nbsp; &nbsp;
        <PlusCircleOutlined onClick={() => styleAppend({})} />
      </td>
    </tr>
  );
};

StyleForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  styleItem: PropTypes.object.isRequired,
  styleIndex: PropTypes.number.isRequired,
  styleAppend: PropTypes.func.isRequired,
  styleRemove: PropTypes.func.isRequired,
};

export default withStyles(styles)(StyleForm);
